import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["selector", "current"]
  
  connect() {
    this.voices = []
    this.selectedVoiceIndex = null
    this.loadVoices()
    
    // Listen for voices changed event
    speechSynthesis.onvoiceschanged = () => this.loadVoices()
  }
  
  loadVoices() {
    this.voices = speechSynthesis.getVoices()
    
    if (this.voices.length === 0) return
    
    // Clear existing options (except auto-select)
    while (this.selectorTarget.children.length > 1) {
      this.selectorTarget.removeChild(this.selectorTarget.lastChild)
    }
    
    // Filter to English voices only and sort by quality
    const englishVoices = this.voices.filter(voice => 
      voice.lang.startsWith('en')
    ).sort((a, b) => {
      // Prioritize higher quality voices
      const aScore = this.getVoiceQualityScore(a)
      const bScore = this.getVoiceQualityScore(b)
      return bScore - aScore
    })
    
    // Add voice options to selector
    englishVoices.forEach((voice, index) => {
      const option = document.createElement('option')
      option.value = this.voices.indexOf(voice)
      option.textContent = `${voice.name} (${voice.lang})`
      this.selectorTarget.appendChild(option)
    })
    
    // Auto-select the best voice if none selected
    if (this.selectedVoiceIndex === null && englishVoices.length > 0) {
      this.selectedVoiceIndex = this.voices.indexOf(englishVoices[0])
      this.updateCurrentDisplay(englishVoices[0])
    }
  }
  
  getVoiceQualityScore(voice) {
    let score = 0
    
    // Premium/Google voices get higher priority
    if (voice.name.includes('Google')) score += 100
    if (voice.name.includes('Microsoft')) score += 80
    
    // macOS voices
    if (['Samantha', 'Alex', 'Daniel', 'Karen', 'Moira'].includes(voice.name)) {
      score += 90
    }
    
    // Prefer US English, then UK, then others
    if (voice.lang === 'en-US') score += 50
    else if (voice.lang === 'en-GB') score += 40
    else if (voice.lang.startsWith('en')) score += 30
    
    // Prefer non-network voices for reliability
    if (!voice.name.includes('Google')) score += 10
    
    return score
  }
  
  changeVoice(event) {
    const value = event.target.value
    
    if (value === 'auto') {
      this.selectedVoiceIndex = null
      this.updateCurrentDisplay()
    } else {
      this.selectedVoiceIndex = parseInt(value)
      if (this.voices[this.selectedVoiceIndex]) {
        this.updateCurrentDisplay(this.voices[this.selectedVoiceIndex])
      }
    }
    
    // Update the global voice selection for the TTS system
    this.updateGlobalVoiceSelection()
  }
  
  updateCurrentDisplay(voice = null) {
    if (voice) {
      this.currentTarget.textContent = voice.name
    } else {
      this.currentTarget.textContent = 'Auto-selected voice'
    }
  }
  
  updateGlobalVoiceSelection() {
    // Update the global selectedVoiceIndex variable used by the TTS system
    if (window.selectedVoiceIndex !== undefined) {
      window.selectedVoiceIndex = this.selectedVoiceIndex
    }
    
    // Also dispatch a custom event for other parts of the system
    document.dispatchEvent(new CustomEvent('voice-selection-changed', {
      detail: {
        voiceIndex: this.selectedVoiceIndex,
        voice: this.selectedVoiceIndex !== null ? this.voices[this.selectedVoiceIndex] : null
      }
    }))
  }
  
  testVoice() {
    const testText = "Hello! This is Axle SI testing your selected voice. How does this sound for our conversations?"
    
    // Create a temporary button element for the speakText function
    const tempButton = document.createElement('button')
    tempButton.setAttribute('data-speaking', 'false')
    
    // Call the global speakText function with our selected voice
    if (typeof window.speakText === 'function') {
      // Temporarily override the global voice selection
      const originalVoiceIndex = window.selectedVoiceIndex
      window.selectedVoiceIndex = this.selectedVoiceIndex
      
      window.speakText(testText, tempButton)
      
      // Restore original selection after test
      setTimeout(() => {
        window.selectedVoiceIndex = originalVoiceIndex
      }, 100)
    } else {
      // Fallback if global function not available
      this.directSpeak(testText)
    }
  }
  
  directSpeak(text) {
    // Direct speech synthesis as fallback
    if (!('speechSynthesis' in window)) {
      this.showMessage('Speech synthesis not supported', 'error')
      return
    }
    
    speechSynthesis.cancel()
    
    const utterance = new SpeechSynthesisUtterance(text)
    utterance.rate = 1.0
    utterance.pitch = 0.95
    utterance.volume = 0.9
    
    if (this.selectedVoiceIndex !== null && this.voices[this.selectedVoiceIndex]) {
      utterance.voice = this.voices[this.selectedVoiceIndex]
    }
    
    speechSynthesis.speak(utterance)
  }
  
  showMessage(message, type) {
    // Create toast notification
    const toast = document.createElement('div')
    const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500'
    
    toast.className = `fixed top-20 right-4 ${bgColor} text-white px-4 py-3 rounded-lg shadow-lg z-50 max-w-sm`
    toast.innerHTML = `
      <div class="flex items-center">
        <i class="fas fa-${type === 'success' ? 'check' : 'exclamation-triangle'} mr-2"></i>
        <span class="text-sm">${message}</span>
      </div>
    `
    
    document.body.appendChild(toast)
    
    // Auto remove after 3 seconds
    setTimeout(() => {
      toast.remove()
    }, 3000)
  }
}